import { useEffect, useState } from 'react'
import { Toast } from './components/shared'
import { APP_DATE, APP_DATE_JA, seedDrivers, seedRecords, seedSettings, seedVehicles } from './data/seed'
import { usePersistentState } from './hooks/usePersistentState'
import LoginScreen from './views/LoginScreen'
import DriverFlow from './views/DriverFlow'
import AdminShell from './views/admin/AdminShell'
import Dashboard from './views/admin/Dashboard'
import RecordsPage from './views/admin/RecordsPage'
import MonthlyReport from './views/admin/MonthlyReport'
import ManagementPage from './views/admin/ManagementPage'
import IntegrationSettings from './views/admin/IntegrationSettings'
import { cloudConfigured } from './lib/supabase'
import {
  restoreCloudSession, saveCloudDriver, saveCloudIntegrations, saveCloudRecord, saveCloudVehicle, signInCloud, signOutCloud, confirmCloudRecord,
} from './lib/cloudStore'
import { getBusinessDate } from './utils/businessDate'
import { managerConfirmation } from './utils/managerConfirmation'
import DriverJournal from './views/DriverJournal'
import JournalsPage from './views/admin/JournalsPage'
import { seedTrips } from './data/tripSeed'
import { TRIPS_KEY, startTrip, finishTrip } from './utils/trips'

export default function App() {
  const [drivers, setDrivers] = usePersistentState('alco-log-drivers-v1', seedDrivers)
  const [vehicles, setVehicles] = usePersistentState('alco-log-vehicles-v1', seedVehicles)
  const [records, setRecords] = usePersistentState('alco-log-records-v4', seedRecords)
  const [settings, setSettings] = usePersistentState('alco-log-settings-v4', seedSettings)
  const [session, setSession] = useState(null)
  const [cloudData, setCloudData] = useState(null)
  const [cloudRestoring, setCloudRestoring] = useState(cloudConfigured)
  const [page, setPage] = useState('dashboard')
  const [toast, setToast] = useState(null)
  const [trips, setTrips] = usePersistentState(TRIPS_KEY, seedTrips)
  const [driverSection, setDriverSection] = useState('alcohol')

  useEffect(() => {
    if (!cloudConfigured) return
    restoreCloudSession()
      .then((data) => { if (data) { setCloudData(data); setSession(data.session) } })
      .catch(() => signOutCloud())
      .finally(() => setCloudRestoring(false))
  }, [])

  const activeDrivers = session?.mode === 'cloud' ? cloudData?.drivers || [] : drivers
  const activeVehicles = session?.mode === 'cloud' ? cloudData?.vehicles || [] : vehicles
  const activeRecords = session?.mode === 'cloud' ? cloudData?.records || [] : records
  const activeSettings = session?.mode === 'cloud' ? cloudData?.settings || seedSettings : settings
  const businessDate = session?.mode === 'cloud' ? getBusinessDate() : { iso: APP_DATE, ja: APP_DATE_JA }

  const login = (nextSession) => {
    setDriverSection('alcohol')
    setSession(nextSession)
    setPage('dashboard')
    window.scrollTo({ top: 0 })
  }

  const cloudLogin = async (email, password) => {
    const data = await signInCloud(email, password)
    setCloudData(data)
    setSession(data.session)
    setPage('dashboard')
    window.scrollTo({ top: 0 })
  }

  const logout = async () => {
    setDriverSection('alcohol')
    if (session?.mode === 'cloud') await signOutCloud()
    setSession(null)
    setCloudData(null)
    window.scrollTo({ top: 0 })
  }

  const changeTrip = async (action, input) => {
    if (session.mode === 'cloud') throw new Error('日報は現在、体験モードのみ対応しています')
    if (session.role !== 'driver' || input.driverId !== session.userId) throw new Error('ドライバーを確認してください')
    const saved = localStorage.getItem(TRIPS_KEY)
    const current = saved ? JSON.parse(saved) : trips
    if (input.date > businessDate.iso) throw new Error('未来の日付は登録できません')
    if (action === 'start' && !activeDrivers.find(d => d.id === session.userId)?.vehicleIds.includes(input.vehicleId)) throw new Error('担当車両を選択してください')
    const next = action === 'start' ? startTrip(current,input) : finishTrip(current,input.id,input)
    localStorage.setItem(TRIPS_KEY,JSON.stringify(next))
    setTrips(next)
    setToast({ title: action === 'start' ? '出発を記録しました' : '帰着・日報を保存しました' })
  }

  const goPage = (nextPage) => {
    setPage(nextPage)
    window.scrollTo({ top: 0 })
  }

  const saveRecord = async (record) => {
    try {
      if (session?.mode === 'cloud') {
        const saved = await saveCloudRecord(record, { organizationId: session.organizationId, drivers: activeDrivers, vehicles: activeVehicles })
        setCloudData((current) => ({ ...current, records: [...current.records, { ...record, id: saved.public_id, dbId: saved.id }] }))
      } else {
        const nextRecords = [...records, record]
        localStorage.setItem('alco-log-records-v4', JSON.stringify(nextRecords))
        setRecords(nextRecords)
      }
      setToast({
        type: record.status === 'review' ? 'info' : 'success',
        title: record.status === 'review' ? '要確認として記録しました' : 'チェックを記録しました',
        message: `${record.phase} ${Number(record.value).toFixed(2)} mg/L`,
      })
    } catch (error) {
      setToast({ type: 'error', title: '記録の保存に失敗しました', message: `${error.message}。入力内容は画面に残っています。` })
      throw error
    }
  }

  const confirmRecord = async (record, details) => {
    if (session.role !== 'admin') throw new Error('管理者でログインしてください')
    const next = managerConfirmation(record, details, session.displayName || '鈴木 管理者')
    if (session.mode === 'cloud') {
      await confirmCloudRecord(next)
      setCloudData(current => ({ ...current, records: current.records.map(item => item.id === next.id ? next : item) }))
    } else {
      const nextRecords = records.map(item => item.id === next.id ? next : item)
      localStorage.setItem('alco-log-records-v4', JSON.stringify(nextRecords))
      setRecords(nextRecords)
    }
    setToast({ title: '管理者の確認を記録しました' })
  }

  const addDriver = async (item) => {
    try {
      if (session?.mode === 'cloud') {
        const saved = await saveCloudDriver(item, session.organizationId, activeVehicles, businessDate.iso)
        setCloudData((current) => ({ ...current, drivers: [...current.drivers, { ...item, id: saved.public_id, dbId: saved.id, active: true }] }))
      } else setDrivers((current) => [...current, item])
      setToast({ title: 'ドライバーを登録しました' })
    } catch (error) { setToast({ type: 'error', title: '登録できませんでした', message: error.message }) }
  }

  const addVehicle = async (item) => {
    try {
      if (session?.mode === 'cloud') {
        const saved = await saveCloudVehicle(item, session.organizationId)
        setCloudData((current) => ({ ...current, vehicles: [...current.vehicles, { ...item, id: saved.public_id, dbId: saved.id, active: true }] }))
      } else setVehicles((current) => [...current, item])
      setToast({ title: '車両を登録しました' })
    } catch (error) { setToast({ type: 'error', title: '登録できませんでした', message: error.message }) }
  }

  const updateSettings = async (nextSettings) => {
    if (session?.mode === 'cloud') {
      await saveCloudIntegrations(nextSettings, session.organizationId)
      setCloudData((current) => ({ ...current, settings: nextSettings }))
    } else setSettings(nextSettings)
  }

  if (cloudRestoring) return <main className="boot-screen"><div className="brand-mark brand-mark--large">ALCO LOG</div><p>クラウド接続を確認しています…</p></main>

  if (!session) return <LoginScreen drivers={drivers} onLogin={login} cloudConfigured={cloudConfigured} onCloudLogin={cloudLogin} />

  if (session.role === 'driver') {
    const driver = activeDrivers.find((item) => item.id === session.userId) || activeDrivers[0]
    return <>{session.mode !== 'cloud' ? <nav className="driver-tools" aria-label="ドライバーメニュー"><button className={driverSection === 'alcohol' ? 'active' : ''} onClick={() => setDriverSection('alcohol')}>アルコール・車両点検</button><button className={driverSection === 'journal' ? 'active' : ''} onClick={() => setDriverSection('journal')}>走行距離・日報</button></nav> : null}<div hidden={driverSection !== 'alcohol'}><DriverFlow driver={driver} vehicles={activeVehicles} settings={activeSettings} records={activeRecords} appDate={businessDate.iso} appDateJa={businessDate.ja} onSave={saveRecord} onLogout={logout} /></div>{session.mode !== 'cloud' ? <div hidden={driverSection !== 'journal'}><DriverJournal driver={driver} drivers={activeDrivers} vehicles={activeVehicles} trips={trips} records={activeRecords} appDate={businessDate.iso} onChange={changeTrip} onAlcohol={() => setDriverSection('alcohol')} onLogout={logout} /></div> : null}<Toast toast={toast} onClose={() => setToast(null)} /></>
  }

  let content = null
  if (page === 'journals') content = session.mode === 'cloud' ? <p>日報・走行距離は現在、体験モードのみ対応しています。</p> : <JournalsPage trips={trips} drivers={activeDrivers} vehicles={activeVehicles} records={activeRecords} defaultMonth={businessDate.iso.slice(0,7)} />
  if (page === 'dashboard') content = <Dashboard drivers={activeDrivers} vehicles={activeVehicles} records={activeRecords} onPage={goPage} appDate={businessDate.iso} onConfirm={confirmRecord} />
  if (page === 'records') content = <RecordsPage drivers={activeDrivers} vehicles={activeVehicles} records={activeRecords} onConfirm={confirmRecord} />
  if (page === 'report') content = <MonthlyReport drivers={activeDrivers} vehicles={activeVehicles} records={activeRecords} settings={activeSettings} defaultMonth={businessDate.iso.slice(0, 7)} createdDateJa={businessDate.ja.replace(/（.+）$/, '')} />
  if (page === 'drivers') content = <ManagementPage type="drivers" items={activeDrivers} vehicles={activeVehicles} onAdd={addDriver} />
  if (page === 'vehicles') content = <ManagementPage type="vehicles" items={activeVehicles} vehicles={activeVehicles} onAdd={addVehicle} />
  if (page === 'integration') content = <IntegrationSettings settings={activeSettings} onSettings={updateSettings} records={activeRecords} drivers={activeDrivers} vehicles={activeVehicles} />

  return (
    <>
      <AdminShell page={page} onPage={goPage} onLogout={logout} dateLabel={businessDate.ja} userName={session.displayName || '鈴木 管理者'}>{content}</AdminShell>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  )
}
